import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { RATING_MAX, type TierId } from '#shared/core/ratings';
import { ratingText, tierFill, tierOf } from '@/lib/rating';
import { uiCopy } from './copy';

/**
 * An attribute bar: label, number, and a flat fill over an outlined pale track (pale, so the
 * white Rotation fill still shows; the fill also ends in an ink edge). Fill is value/99
 * of the track; 0 is an empty outlined track. `null` (or `unsure`) prints ?? in ink soft
 * at 70 percent (the spec says 60; 70 keeps it above 4.5:1 on white). The fill takes the tier
 * color of the value unless `tier` or `fill` says otherwise.
 *
 *   <StatBar label="3PT" value={74} />
 *   <StatBar label="CLU" value={null} />
 */
export interface StatBarProps {
  label: string;
  value: number | null | undefined;
  max?: number;
  tier?: TierId | null;
  /** Any hex fill; wins over tier. */
  fill?: string;
  /** Low confidence: show ?? but keep the bar empty. */
  unsure?: boolean;
  /** Long label for screen readers, "Three point". */
  title?: string;
  className?: string;
}

export function StatBar({ label, value, max = RATING_MAX, tier, fill, unsure, title, className }: StatBarProps) {
  const known = !unsure && value !== null && value !== undefined && Number.isFinite(value);
  const v = known ? Math.min(max, Math.max(0, value)) : 0;
  const pct = max > 0 ? (v / max) * 100 : 0;
  const color = fill ?? tierFill(tier !== undefined ? tier : tierOf(known ? v : null));
  // Grow from zero after mount; the global reduced-motion CSS makes it instant.
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(pct));
    return () => cancelAnimationFrame(id);
  }, [pct]);
  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-display text-sm font-black uppercase italic tracking-wide">{label}</span>
        <span
          className={clsx(
            'tts-display w-[2ch] text-right text-lg tabular-nums leading-none',
            !known && 'text-ink-soft opacity-70',
          )}
        >
          {known ? ratingText(v) : '??'}
        </span>
      </div>
      {/* biome-ignore lint/a11y/useSemanticElements: a native <meter> cannot be drawn in the sticker style on iOS Safari */}
      <div
        role="meter"
        aria-label={title ?? label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={known ? v : undefined}
        aria-valuetext={known ? `${ratingText(v)} of ${max}` : uiCopy.rating.unknown}
        className="h-3.5 w-full overflow-hidden rounded-chip border-(length:--tts-border) border-ink bg-lilac-pale"
      >
        <div
          className="h-full border-r-(length:--tts-border) border-ink"
          style={{
            width: `${shown}%`,
            background: color,
            borderRightWidth: shown > 0 ? undefined : 0,
            transition: 'width 600ms var(--ease-out-expo)',
          }}
        />
      </div>
    </div>
  );
}
